import { useState } from 'react';
import { X, FileSignature, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  type: 'employee' | 'issuer' | 'manager' | 'hod';
  onSign: (data: { signature: string; name?: string; approved?: boolean; comments?: string }) => Promise<void>;
}

const SIGNATURE_TITLES: Record<string, { title: string; description: string }> = {
  employee: {
    title: 'Employee Signature',
    description: 'By signing, you acknowledge receipt of the listed assets and agree to use them responsibly.',
  },
  issuer: {
    title: 'Issuer Signature',
    description: 'By signing, you confirm that you have verified the assets and are issuing them to the employee.',
  },
  manager: {
    title: 'Line Manager Co-signature',
    description: 'By signing, you approve this asset issuance for your team member.',
  },
  hod: {
    title: 'Head of IT Approval',
    description: 'By signing, you give final approval for this asset issuance.',
  },
};

export default function SignatureModal({ open, onClose, type, onSign }: SignatureModalProps) {
  const [signature, setSignature] = useState('');
  const [name, setName] = useState('');
  const [approved, setApproved] = useState(true);
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const config = SIGNATURE_TITLES[type];
  const requiresName = type === 'manager' || type === 'hod';
  const showApproval = type === 'manager' || type === 'hod';
  const showComments = type === 'hod';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!signature.trim()) {
      setError('Please enter your signature');
      return;
    }

    if (requiresName && !name.trim()) {
      setError('Please enter your name');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSign({
        signature: signature.trim(),
        name: name.trim() || undefined,
        approved,
        comments: comments.trim() || undefined,
      });
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Failed to sign');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSignature('');
    setName('');
    setApproved(true);
    setComments('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto m-4">
        <Card className="border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileSignature className="w-5 h-5" />
                {config.title}
              </CardTitle>
              <CardDescription className="mt-1">
                {config.description}
              </CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Error Alert */}
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-destructive/10 border border-destructive/20">
                <AlertCircle className="w-5 h-5 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name (for manager/hod) */}
              {requiresName && (
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Your Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              )}

              {/* Approval Toggle (for manager/hod) */}
              {showApproval && (
                <div className="space-y-2">
                  <Label>Decision</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={cn(
                        "flex-1 p-3 rounded-lg border-2 transition-all",
                        approved
                          ? "border-green-500 bg-green-50 text-green-700"
                          : "border-input hover:border-muted-foreground/50"
                      )}
                      onClick={() => setApproved(true)}
                    >
                      <p className="font-medium">Approve</p>
                    </button>
                    <button
                      type="button"
                      className={cn(
                        "flex-1 p-3 rounded-lg border-2 transition-all",
                        !approved
                          ? "border-red-500 bg-red-50 text-red-700"
                          : "border-input hover:border-muted-foreground/50"
                      )}
                      onClick={() => setApproved(false)}
                    >
                      <p className="font-medium">Reject</p>
                    </button>
                  </div>
                </div>
              )}

              {/* Comments (for hod) */}
              {showComments && (
                <div className="space-y-2">
                  <Label htmlFor="comments">Comments (Optional)</Label>
                  <textarea
                    id="comments"
                    className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Any comments or notes..."
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                  />
                </div>
              )}

              {/* Signature */}
              <div className="space-y-2">
                <Label htmlFor="signature">
                  Digital Signature <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="signature"
                  placeholder="Type your full name as signature"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  className="font-serif italic text-lg"
                />
                <p className="text-xs text-muted-foreground">
                  By typing your name above, you agree this serves as your digital signature.
                </p>
              </div>

              {/* Preview */}
              {signature && (
                <div className="p-4 rounded-lg bg-muted/50 border border-dashed">
                  <p className="text-xs text-muted-foreground mb-2">Signature Preview:</p>
                  <p className="font-serif italic text-2xl text-center">{signature}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  variant={showApproval && !approved ? "destructive" : "default"}
                >
                  {isSubmitting 
                    ? 'Signing...' 
                    : showApproval && !approved 
                      ? 'Reject & Sign'
                      : 'Sign'
                  }
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
